import appConfig from '../config';
import React, { useEffect, useState } from "react";
import { toast } from 'react-toastify';

import {
  Users,
  PiggyBank,
  Wallet,
  UserCheck,
  ArrowUpCircle,
  ArrowDownCircle,
  Calendar,
  X,
  Gamepad2,
  TrendingUp,
} from "lucide-react";

import { getPresetRangeInTimezone } from '../utils/dateUtils';

const baseURL = appConfig.baseURL;

type DatePreset = "today" | "yesterday" | "week" | "month" | "custom" | "all";

const getPresetRange = (
  preset: DatePreset
): { start: string | null; end: string | null } => {
  return getPresetRangeInTimezone(preset);
};

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [myProfile, setMyProfile] = useState<any>(null);

  const getApproxUsd = (amounts: Record<string, number> | undefined, rates: any) => {
    if (!amounts || typeof amounts !== 'object' || Object.keys(amounts).length === 0) return 0;
    let totalUsd = 0;
    const r = rates || {};
    Object.entries(amounts).forEach(([currency, amt]) => {
      if (typeof amt !== 'number' || isNaN(amt)) return;
      const c = (currency || '').toUpperCase();
      if (c === 'USD' || c === 'USDT' || c === 'USDC' || c.startsWith('USDT')) {
        totalUsd += amt;
      } else if (c === 'INR') {
        totalUsd += amt / (r['INR'] || 83.5);
      } else if (c === 'EUR') {
        totalUsd += amt * (r['EUR'] || 1.08);
      } else {
        const rate = r[c] || r[c.replace(/TRC20|ERC20|BEP20|MATIC/g, '')];
        if (rate && !isNaN(rate)) {
          totalUsd += amt * rate;
        } else {
          totalUsd += amt;
        }
      }
    });
    return totalUsd;
  };

  const formatAmt = (num: number) => Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const [activePreset, setActivePreset] = useState<DatePreset>("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  useEffect(() => {
    fetchDashboard(null, null);
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${baseURL}/admin/getMineProfile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data?.success) {
        setMyProfile(data.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDashboard = async (
    startDate: string | null,
    endDate: string | null
  ) => {
    try {
      const token = localStorage.getItem("token");

      let url = `${baseURL}/admin/getDashboardData`;
      const qs: string[] = [];
      if (startDate) qs.push(`startDate=${startDate}`);
      if (endDate) qs.push(`endDate=${endDate}`);
      if (qs.length) url += `?${qs.join("&")}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      setDashboardData(data?.data || {});
    } catch (error) {
      console.log(error);
    }
  };

  const applyPreset = (preset: DatePreset) => {
    setActivePreset(preset);
    setShowCustomPicker(false);

    if (preset === "custom") {
      setShowCustomPicker(true);
      return;
    }

    const { start, end } = getPresetRange(preset);
    fetchDashboard(start, end);
  };

  const applyCustomRange = () => {
    if (!customStart || !customEnd) return;
    setShowCustomPicker(false);
    fetchDashboard(customStart, customEnd);
  };

  const clearFilter = () => {
    setActivePreset("all");
    setCustomStart("");
    setCustomEnd("");
    setShowCustomPicker(false);
    fetchDashboard(null, null);
  };

  const PRESETS: { label: string; value: DatePreset }[] = [
    { label: "All Time", value: "all" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
    { label: "Custom", value: "custom" },
  ];

  const currentRole = (localStorage.getItem("role") || "").toLowerCase();

  const cards = [
    {
      title: activePreset === "all" ? "Total Users" : "Users (Filtered)",
      value: dashboardData?.totalUsers || 0,
      icon: Users,
    },
    {
      title: activePreset === "all" ? "Total Deposit" : "Deposit (Filtered)",
      isCurrency: true,
      breakdown: dashboardData?.totalDeposits || {},
      icon: PiggyBank,
    },
    {
      title: activePreset === "all" ? "Total Withdraw" : "Withdraw (Filtered)",
      isCurrency: true,
      breakdown: dashboardData?.totalWithdrawals || {},
      icon: Wallet,
    },
    {
      title: activePreset === "all" ? "Total Wagered" : "Wagered (Filtered)",
      isCurrency: true,
      breakdown: dashboardData?.totalWagered || {},
      icon: Gamepad2,
    },
    {
      title: activePreset === "all" ? "GGR (Profit)" : "GGR (Filtered)",
      isCurrency: true,
      breakdown: dashboardData?.totalGGR || {},
      icon: TrendingUp,
    },
  ];

  return (
    <div className="p-6 lg:ml-[256px] min-h-screen bg-[#151929] space-y-6">

      {/* heading + date filter */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-sm text-gray-400 mt-1">
            Welcome back! Here's what's happening today.
          </p>
        </div>

        {/* Date filter controls */}
        <div className="flex flex-col items-end gap-2 relative">
          {/* Preset Dropdown */}
          <div className="flex flex-wrap gap-2 justify-end items-center">
            <div className="relative">
              <select
                value={activePreset}
                onChange={(e) => applyPreset(e.target.value as DatePreset)}
                className="
                  appearance-none
                  bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border border-gray-700 text-white text-sm
                  rounded-xl pl-4 pr-10 py-2 outline-none
                  focus:border-indigo-500 transition-colors
                  cursor-pointer
                  [color-scheme:dark]
                "
              >
                {PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                <Calendar size={14} />
              </div>
            </div>

            {activePreset !== "all" && (
              <button
                onClick={clearFilter}
                className="
                  flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium
                  bg-red-500/10 border border-red-500/30 text-red-400
                  hover:bg-red-500/20 hover:text-red-300 transition-all duration-200
                "
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>

          {/* Active filter badge */}
          {dashboardData?.dateFilter && (
            <span className="text-xs text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-3 py-1 mt-1">
              Showing: {dashboardData.dateFilter.startDate}
              {dashboardData.dateFilter.endDate &&
                dashboardData.dateFilter.endDate !==
                  dashboardData.dateFilter.startDate
                  ? ` → ${dashboardData.dateFilter.endDate}`
                  : ""}
            </span>
          )}

          {/* Custom date range picker */}
          {showCustomPicker && (
            <div
              className="
                absolute right-0 top-12 z-50
                flex flex-col gap-3 p-4 rounded-xl
                bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border border-gray-700 shadow-2xl
                w-64
              "
            >
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-400">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="
                    bg-slate-900 border-slate-700/60 shadow-2xl border text-white text-sm
                    rounded-lg px-3 py-1.5 outline-none
                    focus:border-indigo-500 transition-colors
                    [color-scheme:dark]
                  "
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-400">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="
                    bg-slate-900 border-slate-700/60 shadow-2xl border text-white text-sm
                    rounded-lg px-3 py-1.5 outline-none
                    focus:border-indigo-500 transition-colors
                    [color-scheme:dark]
                  "
                />
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={applyCustomRange}
                  disabled={!customStart || !customEnd}
                  className="
                    flex-1 py-1.5 rounded-lg text-sm font-medium
                    bg-indigo-600 text-white
                    hover:bg-indigo-700 transition-colors
                    disabled:opacity-40 disabled:cursor-not-allowed
                  "
                >
                  Apply
                </button>
                <button
                  onClick={() => setShowCustomPicker(false)}
                  className="flex-1 py-1.5 rounded-lg text-sm font-medium
                    bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  text-gray-300
                    hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
      </div>
    </div>

      {myProfile?.role === 'affiliate' && myProfile?.referalcode && (
        <div className="w-full bg-[#1e2337] border border-slate-700/60 rounded-2xl p-6 shadow-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 animate-in fade-in slide-in-from-top-3 duration-300">
          <div className="flex-1">
            <h3 className="text-base font-bold text-white mb-1">Your Referral Program</h3>
            <p className="text-xs text-slate-400">Share your referral link with friends and players to earn commission on their bets.</p>
          </div>
          <div className="flex items-center gap-2 bg-[#151929] border border-slate-700 rounded-xl p-1.5 w-full md:max-w-xl shadow-inner">
            <code className="text-xs text-slate-300 font-mono px-3 break-all select-all flex-1 py-1 truncate">
              {`${appConfig.frontendURL}/register?code=${myProfile.referalcode}`}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${appConfig.frontendURL}/register?code=${myProfile.referalcode}`);
                toast.success("Referral link copied to clipboard!");
              }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs py-2 px-5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className="bg-slate-900 border-slate-700/50 focus:border-indigo-500 border rounded-xl p-5 shadow-lg hover:border-indigo-500 transition-colors flex flex-col justify-start min-h-[140px] relative overflow-hidden group"
            >
              <div className="absolute -right-6 -top-6 text-slate-800/20 group-hover:text-indigo-500/10 transition-colors pointer-events-none">
                <Icon size={120} />
              </div>
              <div className="flex justify-between items-start mb-3 relative z-10">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">{card.title}</p>
                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 shadow-sm flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-indigo-400" />
                </div>
              </div>
              
              <div className="relative z-10 mt-auto">
                {card.isCurrency ? (
                  <div className="flex flex-col">
                    <h2 className="text-2xl font-bold text-white truncate">
                      ≈ ${formatAmt(getApproxUsd(card.breakdown, dashboardData?.rates))}
                    </h2>
                    {card.breakdown && Object.keys(card.breakdown).length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1 custom-scrollbar">
                        {Object.entries(card.breakdown).map(([cur, amt]: any) => (
                          <span key={cur} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-950/80 border border-slate-700 text-slate-300 font-mono tracking-tight whitespace-nowrap">
                            {formatAmt(amt)} <span className="text-slate-500">{cur}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-500 mt-3 font-mono">0.00</span>
                    )}
                  </div>
                ) : (
                  <h2 className="text-2xl font-bold text-white truncate">{card.value}</h2>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* bottom */}
      <div className="bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border border-gray-700 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-2">System Overview</h2>
        <p className="text-sm text-gray-400">
          Your admin panel is running normally.
          Deposits, withdrawals, users and live betting activity are synced.
        </p>
      </div>

    </div>
  );
};

export default AdminDashboard;